/**
 * Created by @author @ddennis - ddennis.dk aka fantastisk.dk/works aka meresukker.dk on 28/04/2025.
 */
import { ConfigQuestionniareGroup, SubjectAssignmentsType } from '../../hooks/useSubjectAssignments'
import { AssignmentType } from '../../Types'
import { ListItemContent } from '../../components/display/ListItemContent'
import { COLOR_PRIMARY } from '../../constants'
import React, { useMemo, useState } from 'react'
import { eachDayOfInterval, format, isAfter, isBefore } from 'date-fns'
import { useMediaQuery } from 'react-responsive'
import { Select } from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'

export const ASSIGNMENT_STATUS = {
  MISSED: 'MISSED',
  COMPLETED: 'COMPLETED',
  PENDING: 'PENDING',
  PARTIAL: 'PARTIAL',
} as const // using the as const - enables us to use as enum

export type ASSIGNMENT_STATUS_TYPE = keyof typeof ASSIGNMENT_STATUS

const borderRadius = 5
const COLOR_GENERATED = '#e8e8e8'
const prettyDateFormat = 'd MMM'

type AssignmentDateType = {
  assignments: AssignmentType[]
  date: string
  status: ASSIGNMENT_STATUS_TYPE
  generated?: boolean
  displayDate: string
}

const STATUS_COLORS = {
  [ASSIGNMENT_STATUS.MISSED]: '#e34a4a',
  [ASSIGNMENT_STATUS.COMPLETED]: COLOR_PRIMARY,
  [ASSIGNMENT_STATUS.PENDING]: COLOR_GENERATED,
}

type ActivityOverviewProps = {
  data: SubjectAssignmentsType['dateMap']
  config: SubjectAssignmentsType['config']
  langKey: string
  questionnaireTypeGroup: ConfigQuestionniareGroup[]
}

/**
 *
 * @param data
 * @param maxDays
 */

export const ActivityOverview = ({ data, config, langKey, questionnaireTypeGroup }: ActivityOverviewProps) => {
  const mediumBreakpoint = useMediaQuery({ query: '(min-width: 1220px)' })
  const maxDays = mediumBreakpoint ? 25 : 20
  const latestQuestionnaire = questionnaireTypeGroup[questionnaireTypeGroup.length - 1].questionnaire

  const chunks: any[] = useMemo(() => {
    const questionnaireIds = questionnaireTypeGroup.map((group) => group.questionnaire._id.toString())
    const { dailys: k } = generatedailyQuestionnaires(data, maxDays, questionnaireIds)
    const _chunks = chunkArray(k, maxDays) || []
    _chunks[0] = padPastDays(_chunks[0], maxDays)

    return _chunks
  }, [maxDays, data, questionnaireTypeGroup])

  const [selected, setSelected] = useState(chunks?.length ? chunks.length - 1 : 0)
  const dailys = useMemo(() => (chunks.length > 0 ? chunks[selected] : []), [chunks, selected])

  const onbordingDate = convertToStrDate(new Date(config.onboardingDate))
  const currentDate = convertToStrDate(new Date())

  const items = chunks
    .map((item, index) => {
      const start = item[0].date
      const end = item[item.length - 1].date

      const startStr = format(new Date(start), 'dd MMMM')
      const endStr = format(new Date(end), 'dd MMMM')

      return {
        value: index,
        label: (
          <div className="d-flex align-items-center py-1">
            <p className="me-2">{startStr} </p>
            <div className="" style={{ background: '#000000', height: 1, width: 16 }}></div>
            <p className="ms-2">{endStr}</p>
          </div>
        ),
      }
    })
    .reverse()

  const handleChange = (value) => {
    setSelected(value)
  }

  const nextSection = () => {
    const value = selected + 1 >= chunks.length ? chunks.length - 1 : selected + 1

    setSelected(value)
  }

  const prevSection = () => {
    const value = selected - 1 <= 0 ? 0 : selected - 1
    setSelected(value)
  }

  return (
    <>
      <div className="col-12 mb-3">
        <div
          className="bg-white d-flex align-items-center justify-content-between py-4 px-3  "
          style={{
            borderBottom: '1px solid',
            borderColor: COLOR_GENERATED,
          }}
        >
          <ListItemContent
            className="ms-1 w-100"
            label="Title:"
            title={latestQuestionnaire.name[langKey]}
          ></ListItemContent>

          <div className="d-flex ">
            <Select
              // small trick to force the select to rerender
              key={items.length}
              options={items}
              value={selected}
              style={{ minWidth: 210 }}
              className="ps-2 border rounded-1 py-1"
              onChange={handleChange}
              variant="borderless"
            />

            <div className="d-flex ms-2 " style={{ padding: 1 }}>
              <div
                onClick={prevSection}
                className="d-flex justify-content-center  align-items-center  pointer"
                style={{
                  border: `1px solid ${COLOR_GENERATED} `,
                  paddingLeft: 12,
                  paddingRight: 12,
                  background: COLOR_GENERATED,
                  borderTopLeftRadius: borderRadius,
                  borderBottomLeftRadius: borderRadius,
                  marginRight: 1.8,
                }}
              >
                <LeftOutlined style={{ fontSize: 14, opacity: selected === 0 ? 0.2 : 1 }} />
              </div>
              <div
                onClick={nextSection}
                className=" d-flex justify-content-center  align-items-center pointer"
                style={{
                  paddingLeft: 12,
                  paddingRight: 12,
                  border: `1px solid ${COLOR_GENERATED} `,
                  borderColor: COLOR_GENERATED,
                  background: COLOR_GENERATED,
                  borderBottomRightRadius: borderRadius,
                  borderTopRightRadius: borderRadius,
                }}
              >
                <RightOutlined style={{ fontSize: 14, opacity: selected === chunks.length - 1 ? 0.2 : 1 }} />
              </div>
            </div>
          </div>
        </div>

        <div
          className="w-100 bg-white d-flex py-2 px-3  "
          style={{
            borderBottom: '1px solid',
            borderColor: COLOR_GENERATED,
          }}
        >
          <div className="row flex-fill overflow-hidden">
            <div className="col-12 d-flex">
              {dailys &&
                dailys.map((item, index) => {
                  const displayDateArr = item?.displayDate.split(' ') || ''

                  // Last item in a list sorted ascending from backend
                  const lastAssignment = item?.assignments?.[item.assignments.length - 1]

                  const pillColor = item.generated
                    ? '#e8e8e8'
                    : computePillColor(lastAssignment?.progress, new Date(), lastAssignment?.endDate)

                  const isTodayOrOnboarding = item.date === currentDate || item.date === onbordingDate
                  const text = item.date === currentDate ? 'Today' : 'Phase start'

                  const link = lastAssignment ? `assignment/` + lastAssignment._id : ''

                  return (
                    <div key={index} className="py-2 w-100 " style={{ paddingLeft: 2, paddingRight: 2 }}>
                      <div
                        className="d-flex flex-column justify-content-center align-items-center mt-2 mb-3 "
                        style={{}}
                      >
                        <p
                          className="text-center text-nowrap mb-0 mt-0 noselect position-absolute text-decoration-none"
                          style={{ fontSize: 10, color: '#515151', opacity: isTodayOrOnboarding ? 1 : 0 }}
                        >
                          {text}
                        </p>
                      </div>
                      <Link
                        to={link}
                        className={`d-flex justify-content-center  ${link ? 'opacityHover' : ''}`}
                        style={{ cursor: link ? 'pointer' : 'default' }}
                      >
                        <div
                          className="rounded-pill  "
                          style={{ background: pillColor, width: 30, minHeight: 18 }}
                        ></div>
                      </Link>
                      <div className="">
                        {index % 2 === 0 ? (
                          <p
                            className="text-center text-nowrap mb-0 mt-2 noselect"
                            style={{ fontSize: 10.5, lineHeight: 1, opacity: 0.8 }}
                          >
                            {displayDateArr[0]} {displayDateArr[1]}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  )
                })}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

const computePillColor = (progress: number, siteTime: Date, endDate: Date) => {
  if (!endDate) {
    return STATUS_COLORS.MISSED
  }

  if (progress === 100) {
    return STATUS_COLORS.COMPLETED
  }

  if (isAfter(siteTime, new Date(endDate))) {
    return STATUS_COLORS.MISSED
  }

  if (isBefore(siteTime, new Date(endDate))) {
    return STATUS_COLORS.PENDING
  }

  return STATUS_COLORS.MISSED
}

const generatedailyQuestionnaires = (data, maxDays: number, questionnaireIdsArr: string[]) => {
  const dailyQuestionnaires = Object.keys(data).map((dateKey) => {
    const itemData = data[dateKey]

    //
    // Filter out anything which is not a daily questionnaires
    // TODO handle if there is more than one daily questionnaire
    //
    const tasks = itemData?.assignments || []
    const onlyDailyTask = tasks.filter((task: AssignmentType) => {
      //return task.recurrenceType === 'DAILY' && task.questionnaireId === qId

      const match = questionnaireIdsArr.indexOf(task.questionnaireId.toString()) > -1
      return task.recurrenceType === 'DAILY' && match
    })

    const strDate = convertToStrDate(new Date(itemData.date))

    const prettyDate = format(new Date(itemData.date), prettyDateFormat)

    return {
      ...itemData,
      date: strDate,
      displayDate: prettyDate,
      assignments: onlyDailyTask,
    } as AssignmentDateType
  })

  const orgCopy = [...dailyQuestionnaires]

  //
  // if assignment collected are less than maxDays - we fill up into the future
  //
  if (dailyQuestionnaires.length < maxDays) {
    const padded = padFurtureDays(dailyQuestionnaires, maxDays)
    return { dailys: dailyQuestionnaires.concat(padded), org: orgCopy }
  }

  return { dailys: dailyQuestionnaires, org: orgCopy }
}

const padPastDays = (firstChunk: AssignmentDateType[], maxDays: number) => {
  const totalDays = firstChunk.length - 1
  const daysToPad = maxDays - totalDays

  if (daysToPad === 0) {
    return firstChunk
  }

  // the last day we have data for
  const lastDay = firstChunk[0].date
  const lastDate = new Date(lastDay)
  lastDate.setDate(lastDate.getDate() - 1)

  // the first of the series of days we need to pad
  const firstDate = new Date(lastDate)

  firstDate.setDate(firstDate.getDate() - daysToPad)

  const daysinInterval = eachDayOfInterval({
    start: firstDate,
    end: lastDate,
  }).map((date) => {
    const strDate = convertToStrDate(new Date(date))
    const prettyDate = format(new Date(date), prettyDateFormat) //convertToStrDate(date)

    return {
      assignments: [],
      date: strDate,
      displayDate: prettyDate,
      status: ASSIGNMENT_STATUS.MISSED,
      generated: true,
    } as AssignmentDateType
  })

  return daysinInterval.concat(firstChunk)
}

const padFurtureDays = (dailyQuestionnaires: AssignmentDateType[], maxDays: number) => {
  const totalDays = dailyQuestionnaires.length - 1
  const daysToPad = maxDays - totalDays

  const lastDay = dailyQuestionnaires[dailyQuestionnaires.length - 1].date
  const lastDate = new Date(lastDay)
  lastDate.setDate(lastDate.getDate() + 1)

  const endDate = new Date()
  endDate.setDate(endDate.getDate() + daysToPad)

  const daysinInterval = eachDayOfInterval({
    start: lastDate,
    end: endDate,
  }).map((date) => {
    const strDate = convertToStrDate(new Date(date))
    const prettyDate = format(new Date(date), prettyDateFormat) //convertToStrDate(date)

    return {
      assignments: [],
      date: strDate,
      displayDate: prettyDate,
      status: ASSIGNMENT_STATUS.MISSED,
      generated: true,
    } as AssignmentDateType
  })

  return daysinInterval
}

const convertToStrDate = (date: Date) => {
  return date.getFullYear() + '/' + (date.getMonth() + 1) + '/' + date.getDate()
}

const chunkArray = (arr, size: number) => {
  if (arr.length - 1 <= size) {
    return [arr]
  }

  const chunks: any[] = []

  const numberOfChunks = Math.ceil(arr.length / size)

  let start = arr.length - 1
  let end = arr.length

  for (let i = 0; i < numberOfChunks; i++) {
    const prevStart = start
    start = Math.max(0, start - size)

    end = Math.max(prevStart, end - size * i)

    const slice = arr.slice(start, end)

    if (slice.length > 0) {
      chunks.unshift(slice)
    }
  }

  return chunks
}
